import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react';
import type { UploadedAsset, AssetKey } from '../types';

// ─── State ───────────────────────────────────────────────────

interface AssetState {
  assets: Partial<Record<AssetKey, UploadedAsset>>;
  uploading: Partial<Record<AssetKey, boolean>>;
  errors: Partial<Record<AssetKey, string>>;
}

const initialState: AssetState = {
  assets: {},
  uploading: {},
  errors: {},
};

// ─── Actions ─────────────────────────────────────────────────

type AssetAction =
  | { type: 'UPLOAD_START'; payload: AssetKey }
  | { type: 'UPLOAD_SUCCESS'; payload: { key: AssetKey; asset: UploadedAsset } }
  | { type: 'UPLOAD_ERROR'; payload: { key: AssetKey; error: string } }
  | { type: 'REMOVE_ASSET'; payload: AssetKey }
  | { type: 'LOAD_ASSETS'; payload: Partial<Record<AssetKey, UploadedAsset>> }
  | { type: 'CLEAR_ALL' };

function assetReducer(state: AssetState, action: AssetAction): AssetState {
  switch (action.type) {
    case 'UPLOAD_START':
      return {
        ...state,
        uploading: { ...state.uploading, [action.payload]: true },
        errors: { ...state.errors, [action.payload]: undefined },
      };

    case 'UPLOAD_SUCCESS':
      return {
        ...state,
        assets: { ...state.assets, [action.payload.key]: action.payload.asset },
        uploading: { ...state.uploading, [action.payload.key]: false },
        errors: { ...state.errors, [action.payload.key]: undefined },
      };

    case 'UPLOAD_ERROR':
      return {
        ...state,
        uploading: { ...state.uploading, [action.payload.key]: false },
        errors: { ...state.errors, [action.payload.key]: action.payload.error },
      };

    case 'REMOVE_ASSET': {
      const assets = { ...state.assets };
      delete assets[action.payload];
      return { ...state, assets };
    }

    case 'LOAD_ASSETS':
      return { ...initialState, assets: action.payload };

    case 'CLEAR_ALL':
      return initialState;

    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────

interface AssetContextValue {
  state: AssetState;
  uploadAsset: (key: AssetKey, asset: UploadedAsset) => void;
  setUploading: (key: AssetKey) => void;
  setUploadError: (key: AssetKey, error: string) => void;
  removeAsset: (key: AssetKey) => void;
  loadAssets: (assets: Partial<Record<AssetKey, UploadedAsset>>) => void;
  clearAll: () => void;
}

const AssetContext = createContext<AssetContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────

export function AssetProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(assetReducer, initialState);

  const uploadAsset = useCallback((key: AssetKey, asset: UploadedAsset) => {
    dispatch({ type: 'UPLOAD_SUCCESS', payload: { key, asset } });
  }, []);

  const setUploading = useCallback((key: AssetKey) => {
    dispatch({ type: 'UPLOAD_START', payload: key });
  }, []);

  const setUploadError = useCallback((key: AssetKey, error: string) => {
    dispatch({ type: 'UPLOAD_ERROR', payload: { key, error } });
  }, []);

  const removeAsset = useCallback((key: AssetKey) => {
    dispatch({ type: 'REMOVE_ASSET', payload: key });
  }, []);

  const loadAssets = useCallback((assets: Partial<Record<AssetKey, UploadedAsset>>) => {
    dispatch({ type: 'LOAD_ASSETS', payload: assets });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
  }, []);

  return (
    <AssetContext.Provider
      value={{ state, uploadAsset, setUploading, setUploadError, removeAsset, loadAssets, clearAll }}
    >
      {children}
    </AssetContext.Provider>
  );
}

export function useAssetContext(): AssetContextValue {
  const ctx = useContext(AssetContext);
  if (!ctx) {
    throw new Error('useAssetContext must be used inside <AssetProvider>');
  }
  return ctx;
}
